<template>
  <b-modal :id="id" @close="clean" @hidden="clean" @ok.prevent="save" :title="title" ok-title="Сохранить" header-bg-variant="dark" header-text-variant="light" ok-variant="dark" size="lg">
    <b-form ref="form">
      <!-- Step 1: SSH Connection -->
      <h5 class="mb-3">SSH подключение</h5>
      <b-form-group label="Хост (IP или FQDN)" :invalid-feedback="invalidHost">
        <b-form-input v-model="host" type="text" placeholder="192.168.0.18" :state="hostState" required />
      </b-form-group>
      <b-row>
        <b-col md="4">
          <b-form-group label="SSH порт" :invalid-feedback="invalidPort">
            <b-form-input v-model.number="sshPort" type="number" min="1" max="65535" :state="portState" required />
          </b-form-group>
        </b-col>
        <b-col md="8">
          <b-form-group label="SSH пользователь" :invalid-feedback="invalidUser">
            <b-form-input v-model="sshUser" type="text" placeholder="root" :state="userState" required />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Метод аутентификации">
        <b-form-radio-group v-model="authMethod" :options="authMethodOptions" name="auth-method" />
      </b-form-group>

      <b-form-group v-if="authMethod === 'key'" label="Приватный SSH ключ (PEM)" description="Ключ будет зашифрован на сервере (Fernet) и НИКОГДА не передан обратно через API">
        <b-form-textarea v-model="sshKey" rows="6" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" />
      </b-form-group>

      <b-form-group v-if="authMethod === 'password'" label="SSH пароль" description="Пароль будет зашифрован и НИКОГДА не передан обратно через API">
        <b-form-input v-model="sshPassword" type="password" :state="passwordState" />
      </b-form-group>

      <b-form-group v-if="sshUser !== 'root'" label="Sudo пароль" description="Нужен если пользователь не root">
        <b-form-input v-model="sudoPassword" type="password" />
      </b-form-group>

      <b-button variant="outline-dark" @click="testConnection" :disabled="!canTest">
        <b-icon icon="hdd-network" /> Тест SSH
      </b-button>

      <b-alert v-if="testResult" :variant="testResult.success ? 'success' : 'danger'" show class="mt-2">
        <strong v-if="testResult.success">SSH подключение успешно!</strong>
        <strong v-else>Ошибка подключения</strong>
        <div v-if="testResult.success">
          Платформа: <code>{{ testResult.platform }}</code><br>
          Python: <code>{{ testResult.python_version }}</code><br>
          Systemd: <code>{{ testResult.systemd ? 'Да' : 'Нет' }}</code>
        </div>
        <div v-else>{{ testResult.error }}</div>
      </b-alert>

      <hr />

      <!-- Step 1.5: SMB / Active Directory credentials (Этап 5.1) -->
      <h5 class="mb-3">
        SMB / Active Directory
        <b-badge variant="info">Этап 5.1</b-badge>
      </h5>
      <b-form-group
        label="AD домен"
        description="Например: CONTOSO.COM. Используется Snaffler / SMBHarvest."
        :invalid-feedback="invalidDomain"
      >
        <b-form-input
          v-model="domain"
          type="text"
          placeholder="CONTOSO.COM"
          :state="domainState"
        />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group
            label="IP Domain Controller"
            description="Опционально. Используется Kerberos-инструментами."
            :invalid-feedback="invalidDcIp"
          >
            <b-form-input
              v-model="dcIp"
              type="text"
              placeholder="192.168.1.10"
              :state="dcIpState"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="SMB / AD пользователь"
            description="Учётка для доступа к SMB-шарам (Snaffler, SMBHarvest)."
          >
            <b-form-input
              v-model="smbUser"
              type="text"
              placeholder="administrator"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group
        label="SMB / AD пароль"
        :invalid-feedback="invalidSmbPassword"
        description="Пароль будет зашифрован Fernet и НИКОГДА не передан обратно через API"
      >
        <b-form-input
          v-model="smbPassword"
          type="password"
          placeholder="Оставьте пустым чтобы не менять"
          :state="smbPasswordState"
        />
      </b-form-group>
      <b-alert v-if="hasSmbAuth" variant="info" show class="mt-2">
        <b-icon icon="info-circle"/>
        Креды будут использоваться инструментами:
        <code>Snaffler</code>, <code>SMBHarvest</code> и будущим AD-аудитом.
        Убедитесь, что на агенте установлен <code>dotnet-runtime-8.0</code> и
        <code>smbclient</code> (см. <code>scripts/install-snaffler-agent.sh</code>).
      </b-alert>

      <hr />

      <!-- Step 2: Install -->
      <h5 class="mb-3">Установка агента</h5>
      <!-- UI-10: Site/Network selection -->
      <b-form-group label="Сайт / Сеть" description="Выберите сайт, к которому относится этот агент (для группировки по сетям)">
        <b-form-select v-model="selectedSite" :options="siteOptions" @change="autoDetectNetwork">
          <template #first>
            <b-form-select-option :value="null">Без сайта</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
      <b-form-text v-if="detectedNetwork" class="mb-2 text-info">
        Автоопределение сети: <code>{{ detectedNetwork }}</code>
      </b-form-text>
      <p class="text-muted small">
        Установка копирует Python-скрипт <code>fix-audit-agent</code> и systemd-юнит <code>fix-audit-agent.service</code> на ноду через SSH, затем запускает сервис.
        Требуется root (или sudo) и systemd.
      </p>
      <b-button variant="dark" @click="installAgent" :disabled="!testResult || !testResult.success || installing">
        <b-spinner v-if="installing" small /> {{ installing ? 'Установка...' : 'Установить агента' }}
      </b-button>

      <b-alert v-if="installResult" :variant="installResult.success ? 'success' : 'danger'" show class="mt-3">
        <strong v-if="installResult.success">Установка завершена успешно!</strong>
        <strong v-else>Ошибка установки: {{ installResult.error }}</strong>
        <pre v-if="installResult.log && installResult.log.length" class="mt-2 p-2 bg-light border rounded small" style="max-height: 200px; overflow-y: auto;">{{ installResult.log.join('\n') }}</pre>
      </b-alert>
    </b-form>
  </b-modal>
</template>

<script>
import RekonoApi from '@/backend/RekonoApi'

export default {
  name: 'agentSetupModal',
  mixins: [RekonoApi],
  props: {
    id: { type: String, required: true },
    agent: { type: Object, required: true },
  },
  data () {
    return {
      // SSH fields
      host: '',
      sshPort: 22,
      sshUser: 'root',
      authMethod: 'password',
      sshKey: '',
      sshPassword: '',
      sudoPassword: '',

      // SMB / AD fields (Этап 5.1)
      domain: '',
      dcIp: '',
      smbUser: '',
      smbPassword: '',

      // Validation
      hostState: null,
      portState: null,
      userState: null,
      passwordState: null,
      invalidHost: '',
      invalidPort: '',
      invalidUser: '',
      domainState: null,
      dcIpState: null,
      smbPasswordState: null,
      invalidDomain: '',
      invalidDcIp: '',
      invalidSmbPassword: '',

      // Test/Install state
      testResult: null,
      installResult: null,
      installing: false,

      // UI-10: Site selection
      selectedSite: null,
      siteOptions: [],
      detectedNetwork: '',
    }
  },
  computed: {
    title () {
      return `Настройка агента: ${this.agent.name}`
    },
    canTest () {
      return this.host && this.sshUser && (this.authMethod === 'key' ? this.sshKey : this.sshPassword)
    },
    authMethodOptions () {
      return [
        { value: 'password', text: 'Пароль' },
        { value: 'key', text: 'SSH ключ (PEM)' },
      ]
    },
    // SMB / AD helpers (Этап 5.1)
    hasSmbAuth () {
      // All three must be present for the Snaffler/SMBHarvest backend to
      // consider the credentials complete.
      return Boolean(this.domain && this.smbUser && this.smbPassword)
    },
    smbAuthIncomplete () {
      // Some but not all three are filled in — show a soft warning.
      const filled = [this.domain, this.smbUser, this.smbPassword].filter(Boolean).length
      return filled > 0 && filled < 3
    },
  },
  mounted () {
    this.loadSites()
    if (this.agent && this.agent.site) {
      this.selectedSite = this.agent.site
    }
    // Pre-fill SSH + SMB fields from existing credentials metadata
    if (this.agent) {
      this.host = this.agent.host || ''
      this.sshPort = 22
      this.sshUser = 'root'
      // Load existing credentials metadata
      this.get(`/api/agents/${this.agent.id}/credentials/`)
        .then(response => {
          const creds = response.data
          if (creds.host) this.host = creds.host
          if (creds.ssh_port) this.sshPort = creds.ssh_port
          if (creds.ssh_user) this.sshUser = creds.ssh_user
          if (creds.auth_method === 'key') this.authMethod = 'key'
          else if (creds.auth_method === 'password') this.authMethod = 'password'
          // SMB / AD (Этап 5.1)
          if (creds.domain) this.domain = creds.domain
          if (creds.dc_ip) this.dcIp = creds.dc_ip
          if (creds.smb_user) this.smbUser = creds.smb_user
          // Note: smb_password is NEVER returned by the API — it's write-only.
        })
        .catch(() => { /* no credentials yet */ })
    }
  },
  methods: {
    // UI-10: Load sites
    loadSites () {
      this.get('/api/sites/')
        .then(response => {
          this.siteOptions = response.data.results.map(site => ({
            value: site.id, text: `${site.name} (${site.network || 'без сети'})`
          }))
        })
        .catch(() => { this.siteOptions = [] })
    },
    autoDetectNetwork () {
      // Auto-detect network from SSH host
      if (this.host) {
        const parts = this.host.split('.')
        if (parts.length === 4) {
          this.detectedNetwork = `${parts[0]}.${parts[1]}.${parts[2]}.0/24`
        }
      }
    },
    // Validation
    validateHost () {
      if (!this.host || this.host.length === 0) {
        this.hostState = false
        this.invalidHost = 'Хост обязателен'
        return false
      }
      // Simple IP/hostname check
      const re = /^(\d{1,3}\.){3}\d{1,3}$|^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$|^[a-zA-Z0-9]+$/
      if (!re.test(this.host)) {
        this.hostState = false
        this.invalidHost = 'Неверный формат IP или имени'
        return false
      }
      this.hostState = true
      return true
    },
    validatePort () {
      if (this.sshPort < 1 || this.sshPort > 65535) {
        this.portState = false
        this.invalidPort = 'Порт 1-65535'
        return false
      }
      this.portState = true
      return true
    },
    validateUser () {
      if (!this.sshUser) {
        this.userState = false
        this.invalidUser = 'Пользователь обязателен'
        return false
      }
      this.userState = true
      return true
    },
    // SMB / AD validators (Этап 5.1)
    validateDomain () {
      if (!this.domain) {
        this.domainState = null
        this.invalidDomain = ''
        return true                                       // empty is OK (not required)
      }
      // Loose check: at least one dot, only letters/digits/hyphens/dots/underscores.
      const re = /^[A-Za-z0-9_.-]+\.[A-Za-z0-9_.-]+$/
      if (!re.test(this.domain)) {
        this.domainState = false
        this.invalidDomain = 'Неверный формат домена (например, CONTOSO.COM)'
        return false
      }
      this.domainState = true
      return true
    },
    validateDcIp () {
      if (!this.dcIp) {
        this.dcIpState = null
        this.invalidDcIp = ''
        return true
      }
      const re = /^(\d{1,3}\.){3}\d{1,3}$/
      if (!re.test(this.dcIp)) {
        this.dcIpState = false
        this.invalidDcIp = 'Неверный формат IP (например, 192.168.1.10)'
        return false
      }
      this.dcIpState = true
      return true
    },
    validateSmbPassword () {
      // Password is only required when the user filled in domain/user.
      // We don't validate strength here — that's the operator's responsibility.
      if (!this.smbPassword) {
        this.smbPasswordState = null
        this.invalidSmbPassword = ''
        return true
      }
      this.smbPasswordState = true
      return true
    },
    check () {
      const v1 = this.validateHost()
      const v2 = this.validatePort()
      const v3 = this.validateUser()
      const v4 = this.validateDomain()
      const v5 = this.validateDcIp()
      const v6 = this.validateSmbPassword()
      return v1 && v2 && v3 && v4 && v5 && v6
    },

    // Save credentials
    save (event) {
      event.preventDefault()
      if (!this.check()) return

      const payload = {
        host: this.host,
        ssh_port: this.sshPort,
        ssh_user: this.sshUser,
      }
      if (this.authMethod === 'key' && this.sshKey) {
        payload.ssh_key = this.sshKey
      } else if (this.authMethod === 'password' && this.sshPassword) {
        payload.ssh_password = this.sshPassword
      }
      if (this.sudoPassword) {
        payload.sudo_password = this.sudoPassword
      }

      // SMB / AD (Этап 5.1) — only send fields that are filled in. Empty
      // strings clear the corresponding field server-side.
      if (this.domain) payload.domain = this.domain.trim().toUpperCase()
      if (this.dcIp) payload.dc_ip = this.dcIp.trim()
      if (this.smbUser) payload.smb_user = this.smbUser.trim()
      if (this.smbPassword) payload.smb_password = this.smbPassword

      this.put(`/api/agents/${this.agent.id}/credentials-set/`, payload, this.agent.name, 'Креды сохранены (SSH + SMB/AD)')
        .then(() => {
          // UI-10: Also update agent's site
          if (this.selectedSite !== null) {
            return this.put(`/api/agents/${this.agent.id}/`, { name: this.agent.name, site: this.selectedSite })
          }
        })
        .then(() => {
          // Wipe the plaintext passwords from local state — they live only on
          // the server now (encrypted).
          this.sshPassword = ''
          this.sudoPassword = ''
          this.smbPassword = ''
          this.$emit('saved')
        })
        .catch(err => {
          console.error('Save credentials failed:', err)
        })
    },

    // Test SSH connection — first save credentials, then test
    testConnection () {
      this.testResult = null
      if (!this.check()) return

      // Build credentials payload from form
      const payload = {
        host: this.host,
        ssh_port: this.sshPort,
        ssh_user: this.sshUser,
      }
      if (this.authMethod === 'key' && this.sshKey) {
        payload.ssh_key = this.sshKey
      } else if (this.authMethod === 'password' && this.sshPassword) {
        payload.ssh_password = this.sshPassword
      }
      if (this.sudoPassword) {
        payload.sudo_password = this.sudoPassword
      }
      // SMB creds travel with the SSH save too — they're stored together.
      if (this.domain) payload.domain = this.domain.trim().toUpperCase()
      if (this.dcIp) payload.dc_ip = this.dcIp.trim()
      if (this.smbUser) payload.smb_user = this.smbUser.trim()
      if (this.smbPassword) payload.smb_password = this.smbPassword

      // Save credentials first, then test SSH
      this.put(`/api/agents/${this.agent.id}/credentials-set/`, payload, this.agent.name, null)
        .then(() => {
          return this.post(`/api/agents/${this.agent.id}/test-connection/`, null, this.agent.name, null)
        })
        .then(data => {
          this.testResult = data
        })
        .catch(err => {
          this.testResult = { success: false, error: (err.response?.data?.error || err.message || 'Ошибка сохранения кредов') }
        })
    },

    // Install agent via SSH
    installAgent () {
      this.installing = true
      this.installResult = null
      this.post(`/api/agents/${this.agent.id}/install/`, null, this.agent.name, null)
        .then(data => {
          this.installResult = data
          if (data.success) {
            this.$emit('installed')
          }
        })
        .catch(err => {
          this.installResult = { success: false, error: (err.response?.data?.error || err.message) }
        })
        .finally(() => {
          this.installing = false
        })
    },

    clean () {
      this.host = ''
      this.sshPort = 22
      this.sshUser = 'root'
      this.authMethod = 'password'
      this.sshKey = ''
      this.sshPassword = ''
      this.sudoPassword = ''
      this.testResult = null
      this.installResult = null
      this.hostState = null
      this.portState = null
      this.userState = null
      this.selectedSite = null
      this.detectedNetwork = ''
      this.$emit('clean')
    },
  },
}
</script>
